"use client";

import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type {
  HUBBLE_LAW_CALCULATOR_STRINGS,
  HubbleMode,
} from "@/components/tools/hubble-law-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  C_KM_S,
  comovingDistanceMpc,
  distanceFromVelocity,
  H0_CHOICES,
  hubbleDistanceMpc,
  hubbleTimeGyr,
  mpcToLightYears,
  velocityFromDistance,
  velocityLowRedshift,
  velocityRelativistic,
} from "@/lib/tool-kit/astronomy/hubble";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";

type Strings = typeof HUBBLE_LAW_CALCULATOR_STRINGS;

/** The low-redshift limit beyond which Hubble's law is flagged. */
const LOW_REDSHIFT = 0.1;

interface Outcome {
  velocity: number;
  distanceMpc: number;
}

/** Velocity (km/s) and distance (Mpc) for one H₀ and the reader's input. */
function outcomeFor(mode: HubbleMode, h0: number, input: number): Outcome {
  if (mode === "velocity") {
    return { velocity: velocityFromDistance(h0, input), distanceMpc: input };
  }
  if (mode === "distance") {
    return { velocity: input, distanceMpc: distanceFromVelocity(h0, input) };
  }
  const velocity = velocityLowRedshift(input);
  return { velocity, distanceMpc: distanceFromVelocity(h0, velocity) };
}

interface Row {
  choice: (typeof H0_CHOICES)[number];
  out: Outcome;
}

function CompareTable({
  mode,
  rows,
  strings,
}: {
  mode: HubbleMode;
  rows: Row[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const show = (value: number) => formatSig(fmt, value, 5);
  return (
    <div className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{strings.compareHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.sourceColumn}
              </th>
              <th className={TH} scope="col">
                {strings.valueColumn}
              </th>
              <th className={TH} scope="col">
                {strings.resultColumn}
              </th>
              <th className={TH} scope="col">
                {strings.timeColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ choice, out }) => (
              <tr key={choice.id}>
                <td className={TD}>
                  {(strings.h0Names as Record<string, string>)[choice.id]}
                </td>
                <td className={TD}>
                  {choice.sigma > 0
                    ? t(strings.plusMinus, {
                        value: fmt(choice.value, { maximumFractionDigits: 2 }),
                        sigma: fmt(choice.sigma, { maximumFractionDigits: 2 }),
                      })
                    : fmt(choice.value)}
                </td>
                <td className={TD}>
                  {mode === "distance"
                    ? t(strings.mpcValue, { value: show(out.distanceMpc) })
                    : t(strings.kms, { value: show(out.velocity) })}
                </td>
                <td className={TD}>{show(hubbleTimeGyr(choice.value))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Caveats({
  h0,
  input,
  redshift,
  velocity,
  distanceMpc,
  strings,
}: {
  h0: number;
  input: number;
  redshift: boolean;
  velocity: number;
  distanceMpc: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const show = (value: number) => formatSig(fmt, value, 5);
  const lcdm = redshift && input > 0 ? comovingDistanceMpc(input, h0) : null;
  return (
    <>
      {velocity > C_KM_S ? <Notice>{strings.fasterThanLight}</Notice> : null}
      {redshift && input > LOW_REDSHIFT ? (
        <Notice>{strings.lowRedshiftWarning}</Notice>
      ) : null}
      {lcdm !== null && distanceMpc > 0 ? (
        <p className="text-foreground text-sm">
          {t(strings.lcdmLine, {
            z: show(input),
            mpc: show(lcdm),
            percent: fmt(Math.abs(lcdm / distanceMpc - 1) * 100, {
              maximumFractionDigits: 1,
            }),
            direction: lcdm < distanceMpc ? strings.smaller : strings.larger,
          })}
        </p>
      ) : null}
    </>
  );
}

export function HubbleResults({
  mode,
  h0,
  sigma,
  input,
  strings,
}: {
  mode: HubbleMode;
  h0: number | null;
  sigma: number;
  input: number | null;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const show = (value: number) => formatSig(fmt, value, 5);
  if (h0 === null || h0 <= 0) {
    return <ErrorNotice>{strings.badH0}</ErrorNotice>;
  }
  if (input === null || input < 0) {
    return <Notice>{strings.missing}</Notice>;
  }
  const now = outcomeFor(mode, h0, input);
  const redshift = mode === "redshift";
  const lyOf = (mpc: number) => mpcToLightYears(mpc);
  const low = outcomeFor(mode, h0 + sigma, input);
  const high = outcomeFor(mode, Math.max(h0 - sigma, h0 * 0.01), input);
  const headline = mode === "distance" ? now.distanceMpc : now.velocity;
  const headlineText =
    mode === "distance"
      ? t(strings.mpcValue, { value: show(headline) })
      : t(strings.kms, { value: show(headline) });
  const [lowText, highText] =
    mode === "distance"
      ? [
          t(strings.mpcValue, { value: show(high.distanceMpc) }),
          t(strings.mpcValue, { value: show(low.distanceMpc) }),
        ]
      : [
          t(strings.kms, { value: show(low.velocity) }),
          t(strings.kms, { value: show(high.velocity) }),
        ];
  const rows = H0_CHOICES.map((choice) => ({
    choice,
    out: outcomeFor(mode, choice.value, input),
  }));
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={mode === "distance" ? strings.distanceMpc : strings.velocity}
          value={headlineText}
        />
        {redshift ? (
          <StatCard
            label={strings.velocityRel}
            value={t(strings.kms, { value: show(velocityRelativistic(input)) })}
          />
        ) : null}
        <StatCard
          label={strings.fractionOfC}
          value={t(strings.fractionValue, {
            value: show(now.velocity / C_KM_S),
          })}
        />
        {redshift ? (
          <StatCard label={strings.distanceMpc} value={show(now.distanceMpc)} />
        ) : null}
        <StatCard
          label={strings.distanceGly}
          value={t(strings.lightYears, { value: show(lyOf(now.distanceMpc)) })}
        />
        <StatCard
          label={strings.hubbleTime}
          value={t(strings.hubbleTimeValue, { value: show(hubbleTimeGyr(h0)) })}
        />
        <StatCard
          label={strings.hubbleDistance}
          value={t(strings.hubbleDistanceValue, {
            value: show(hubbleDistanceMpc(h0)),
          })}
        />
      </StatGrid>
      {sigma > 0 ? (
        <p className="text-foreground text-sm">
          <span className="font-medium">{strings.rangeHeading}: </span>
          {t(strings.rangeLine, {
            h0: show(h0),
            sigma: show(sigma),
            low: lowText,
            high: highText,
          })}
        </p>
      ) : null}
      <Caveats
        distanceMpc={now.distanceMpc}
        h0={h0}
        input={input}
        redshift={redshift}
        strings={strings}
        velocity={now.velocity}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.summary, {
          h0: show(h0),
          input: mode === "redshift" ? `z = ${show(input)}` : show(input),
          output: headlineText,
        })}
      />
      <CompareTable mode={mode} rows={rows} strings={strings} />
      <p className="text-muted-foreground text-xs">{strings.note}</p>
    </section>
  );
}
